import { useEffect, useRef, useState, useCallback } from 'react';

const galleryVideos = [
  {
    src: '/event-pask-2026.mp4',
    title: 'P\u00e5sk Tillsammans',
    year: '2026',
    poster: '/gallery-1.jpg',
  },
  {
    src: '/event-jul-2025.mp4',
    title: 'Jul Tillsammans',
    year: '2025',
    poster: '/gallery-4.jpg',
  },
  {
    src: '/event-jul-2024.mp4',
    title: 'Jul Tillsammans',
    year: '2024',
    poster: '/gallery-7.jpg',
  },
];

const galleryImages = [
  { src: '/gallery-1.jpg', alt: 'Gemenskap vid matbordet' },
  { src: '/gallery-2.jpg', alt: 'Ansiktsmålning' },
  { src: '/gallery-3.jpg', alt: 'Barn och volontärer' },
  { src: '/gallery-4.jpg', alt: 'Julbuffé' },
  { src: '/gallery-5.jpg', alt: 'Event i full gång' },
  { src: '/gallery-6.jpg', alt: 'Frisör på plats' },
  { src: '/gallery-7.jpg', alt: 'Tomten och barn' },
  { src: '/gallery-8.jpg', alt: 'Gåvoutdelning' },
  { src: '/gallery-9.jpg', alt: 'Servering av mat' },
  { src: '/gallery-10.jpg', alt: 'Skratt och glädje' },
  { src: '/gallery-11.jpg', alt: 'Frisörstationen' },
  { src: '/gallery-12.jpg', alt: 'Sponsring och dryck' },
  { src: '/gallery-13.jpg', alt: 'Frisörutrustning' },
  { src: '/gallery-14.jpg', alt: 'DJ och musik' },
  { src: '/gallery-15.jpg', alt: 'Köket i full gång' },
  { src: '/gallery-16.jpg', alt: 'I AM THE WAY hoodie' },
];

function VideoCard({ video, autoplay }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!autoplay) return;
    const el = videoRef.current;
    if (!el) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      });
    }, { threshold: 0.4 });

    observer.observe(el);
    return () => observer.disconnect();
  }, [autoplay]);

  const handleFullscreen = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.webkitEnterFullscreen) el.webkitEnterFullscreen();
  };

  return (
    <div className="gallery-block gallery-video-item">
      <div className="gallery-video-meta">
        <span className="gallery-video-year">{video.year}</span>
        <h3 className="gallery-video-title">{video.title}</h3>
      </div>
      <div className="gallery-video-wrap">
        <video
          ref={videoRef}
          preload="metadata"
          poster={video.poster}
          autoPlay={autoplay}
          muted
          loop
          playsInline
          controls={!autoplay}
        >
          <source src={video.src} type="video/mp4" />
          Din webbl&auml;sare st&ouml;der inte video-elementet.
        </video>
        <button className="video-fullscreen-btn" onClick={handleFullscreen} aria-label="Helsk\u00e4rm">
          <i className="fas fa-expand"></i>
        </button>
      </div>
    </div>
  );
}

function Gallery() {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const touchStart = useRef(null);

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);

  const goPrev = useCallback(() => {
    setLightboxIndex(i => (i - 1 + galleryImages.length) % galleryImages.length);
  }, []);

  const goNext = useCallback(() => {
    setLightboxIndex(i => (i + 1) % galleryImages.length);
  }, []);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [lightboxIndex, goPrev, goNext]);

  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('fade-in-up');
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    document.querySelectorAll('.gallery-block')
      .forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      diff > 0 ? goNext() : goPrev();
    }
    touchStart.current = null;
  };

  const scrollItems = [...galleryImages, ...galleryImages];

  return (
    <div className="gallery-page">
      {/* Hero */}
      <section className="gallery-hero">
        <div className="container">
          <div className="gallery-hero-text">
            <span className="section-tag">Galleri</span>
            <h1>&Ouml;gonblick fr&aring;n v&aring;ra event.</h1>
            <p>Se gl&auml;djen vi har spridit genom &aring;ren.</p>
          </div>
        </div>
      </section>

      {/* Videos - latest first */}
      <section className="gallery-video-section">
        <div className="container">
          <div className="gallery-block gallery-videos-header">
            <h2>Filmer fr&aring;n v&aring;ra event</h2>
            <p>Senaste eventet h&ouml;gst upp.</p>
          </div>
          <div className="gallery-videos-list">
            {galleryVideos.map((v, idx) => (
              <VideoCard key={v.src} video={v} autoplay={idx === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* Photo grid */}
      <section className="gallery-photos">
        <div className="container">
          <div className="gallery-block gallery-photos-header">
            <h2>Bilder fr&aring;n v&aring;ra event</h2>
            <p>Minnen som vi b&auml;r med oss</p>
          </div>
          <div className="gallery-grid">
            {galleryImages.map((img, idx) => (
              <div className="gallery-grid-item" key={idx} onClick={() => openLightbox(idx)}>
                <img src={img.src} alt={img.alt} loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scrolling strip */}
      <section className="gallery-strip">
        <div className="gallery-strip-track">
          {scrollItems.map((img, idx) => (
            <div className="gallery-strip-item" key={idx}>
              <img src={img.src} alt={img.alt} loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="lightbox-overlay"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Stäng">&times;</button>
          <button className="lightbox-prev" onClick={(e) => { e.stopPropagation(); goPrev(); }} aria-label="Föregående">&#8249;</button>
          <img
            className="lightbox-img"
            src={galleryImages[lightboxIndex].src}
            alt={galleryImages[lightboxIndex].alt}
            onClick={(e) => e.stopPropagation()}
          />
          <button className="lightbox-next" onClick={(e) => { e.stopPropagation(); goNext(); }} aria-label="Nästa">&#8250;</button>
          <span className="lightbox-counter">{lightboxIndex + 1} / {galleryImages.length}</span>
        </div>
      )}
    </div>
  );
}

export default Gallery;
